// FieldVision - App.tsx - DEBUG VERSION
// Med console.logs for handleNavigateToFieldFromOversigt
// FIX: MyFields back-knap navigerer nu til mine-marker-oversigt (2025-12-31)

import { useState, useEffect } from 'react';
import './App.css';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPasswordRequest from './components/auth/ResetPasswordRequest';
import ResetPasswordConfirm from './components/auth/ResetPasswordConfirm';
import Dashboard from './components/Dashboard';
import MyFields from './components/MyFields';
import BookingWizard from './components/BookingWizard';
import MapView from './components/MapView';
import Header from './components/Header';
import ProfileModal from './components/ProfileModal';
import MarkDetaljePage from './components/MarkDetaljePage';
import MineMarkerOversigt from './components/MineMarkerOversigt';
import MineLogfilerOversigt from './components/MineLogfilerOversigt';
import {
  getFieldId,
  hasFeatureWithId,
  migrateLocalStorageFieldIds,
  migrateLocalStorageZoomFields
} from './utils/fieldUtils';
import apiService from './services/api';

type View = 'login' | 'register' | 'resetRequest' | 'resetConfirm' | 'dashboard' | 'myFields' | 'booking' | 'fieldDetail' | 'mineMarkerOversigt' | 'mineLogfilerOversigt';

// FIX: Udvidet interface med geometry til field outline
interface FieldDetailContext {
  fieldId?: string;
  orderId?: number;
  fieldData?: {
    marknr: string;
    cvr: string;
    areal: number;
    id?: number;
    geometry?: string;
  };
  orderData?: any;
  source: 'order' | 'field' | 'oversigt';
}

const getStorageKeys = (userId?: number) => {
  if (!userId) return null;
  return {
    CURRENT_CVR: `fieldvision_user_${userId}_currentCVR`,
    GEOJSON: `fieldvision_user_${userId}_geojson`,
    SELECTED_FIELDS: `fieldvision_user_${userId}_selectedFields`,
    ZOOM_FIELDS: `fieldvision_user_${userId}_zoomFields`
  };
};

function AppContent() {
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const [currentView, setCurrentView] = useState<View>(isAuthenticated ? 'dashboard' : 'login');
  const [currentCVR, setCurrentCVR] = useState<string>('');
  const [geojson, setGeojson] = useState<any>(null);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [zoomLoadedFields, setZoomLoadedFields] = useState<any[]>([]);
  const [bookingSelectedFields, setBookingSelectedFields] = useState<string[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>('');
  const [fieldDetailContext, setFieldDetailContext] = useState<FieldDetailContext | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      const migratedFields = migrateLocalStorageFieldIds((user as any).userId);
      const migratedZoom = migrateLocalStorageZoomFields((user as any).userId);

      if (migratedFields || migratedZoom) {
        console.log('FieldVision: Migrated old localStorage format');
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const keys = getStorageKeys((user as any).userId);
      if (keys) {
        const storedCVR = localStorage.getItem(keys.CURRENT_CVR);
        const storedGeojson = localStorage.getItem(keys.GEOJSON);
        const storedFields = localStorage.getItem(keys.SELECTED_FIELDS);
        const storedZoomFields = localStorage.getItem(keys.ZOOM_FIELDS);

        if (storedCVR) setCurrentCVR(storedCVR);
        if (storedGeojson) {
          try {
            setGeojson(JSON.parse(storedGeojson));
          } catch (e) {
            console.error('Failed to parse stored geojson:', e);
          }
        }
        if (storedFields) {
          try {
            setSelectedFieldIds(JSON.parse(storedFields));
          } catch (e) {
            console.error('Failed to parse stored fields:', e);
          }
        }
        if (storedZoomFields) {
          try {
            setZoomLoadedFields(JSON.parse(storedZoomFields));
          } catch (e) {
            console.error('Failed to parse stored zoom fields:', e);
          }
        }
      }
    }
  }, [isAuthenticated, user]);

  // Valider cached currentCVR mod API - fjern CVR'er brugeren ikke har adgang til
  useEffect(() => {
    if (isAuthenticated && user && currentCVR) {
      apiService.getUserCVRs().then(result => {
        const validCVRs = new Set(result.cvrs);
        const cachedCVRs = currentCVR.split(',').map((c: string) => c.trim());
        const filtered = cachedCVRs.filter((c: string) => validCVRs.has(c));
        if (filtered.length !== cachedCVRs.length) {
          console.log('[App] Renser ugyldige CVRs fra cache:', 
            cachedCVRs.filter((c: string) => !validCVRs.has(c)));
          setCurrentCVR(filtered.join(', '));
          // Ryd også selectedCVRs i localStorage
          const storedSelected = localStorage.getItem('fieldvision_selectedCVRs');
          if (storedSelected) {
            try {
              const selectedArr = JSON.parse(storedSelected);
              const validSelected = selectedArr.filter((c: string) => validCVRs.has(c));
              if (validSelected.length !== selectedArr.length) {
                localStorage.setItem('fieldvision_selectedCVRs', JSON.stringify(validSelected));
              }
            } catch (e) { /* ignore parse errors */ }
          }
        }
      }).catch(err => {
        console.error('[App] Kunne ikke validere CVRs:', err);
      });
    }
  }, [isAuthenticated, user, currentCVR]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const keys = getStorageKeys((user as any).userId);
      if (keys) {
        if (currentCVR) {
          localStorage.setItem(keys.CURRENT_CVR, currentCVR);
        } else {
          localStorage.removeItem(keys.CURRENT_CVR);
        }
      }
    }
  }, [currentCVR, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const keys = getStorageKeys((user as any).userId);
      if (keys) {
        if (geojson) {
          localStorage.setItem(keys.GEOJSON, JSON.stringify(geojson));
        } else {
          localStorage.removeItem(keys.GEOJSON);
        }
      }
    }
  }, [geojson, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const keys = getStorageKeys((user as any).userId);
      if (keys) {
        if (selectedFieldIds.length > 0) {
          localStorage.setItem(keys.SELECTED_FIELDS, JSON.stringify(selectedFieldIds));
        } else {
          localStorage.removeItem(keys.SELECTED_FIELDS);
        }
      }
    }
  }, [selectedFieldIds, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const keys = getStorageKeys((user as any).userId);
      if (keys) {
        if (zoomLoadedFields.length > 0) {
          localStorage.setItem(keys.ZOOM_FIELDS, JSON.stringify(zoomLoadedFields));
        } else {
          localStorage.removeItem(keys.ZOOM_FIELDS);
        }
      }
    }
  }, [zoomLoadedFields, isAuthenticated, user]);

  useEffect(() => {
    // FIX: Inkluder både 'login' OG 'register' i redirect check
    // Så dashboard vises korrekt efter første registrering
    if (isAuthenticated && (currentView === "login" || currentView === "register")) {
      setCurrentView("dashboard");
    } else if (!isAuthenticated && !['login', 'register', 'resetRequest', 'resetConfirm'].includes(currentView)) {
      setCurrentView("login");
    }
  }, [isAuthenticated, currentView]);

  const handleFieldsLoaded = (data: any, cvrs: string[]) => {
    setGeojson(data);
    setCurrentCVR(cvrs.join(", "));
    
    // Auto-vælg alle marker fra de nye CVR'er
    if (data?.features) {
      const allFieldIds = data.features.map((f: any) => {
        const cvr = f.properties?.CVR || '';
        const marknr = f.properties?.Marknr || '';
        return `${cvr}-${marknr}`;
      }).filter((id: string) => id !== '-');
      setSelectedFieldIds(allFieldIds);
    }
  };

  const handleNavigate = (view: 'myFields' | 'booking' | 'mineMarkerOversigt' | 'mineLogfilerOversigt') => {
    if (view === 'booking') {
      setBookingSelectedFields([]);
    }
    setCurrentView(view);
  };

  const handleNavigateToFieldDetail = (id: number, data?: any) => {
    const isOrder = data && ('orderId' in data || 'orderNumber' in data || 'serviceType' in data);

    if (isOrder) {
      setFieldDetailContext({
        orderId: id,
        orderData: data,
        source: 'order'
      });
    } else {
      setFieldDetailContext({
        fieldId: id.toString(),
        fieldData: data,
        source: 'field'
      });
    }
    setCurrentView('fieldDetail');
  };

  // DEBUG VERSION: Handler for navigation fra MineMarkerOversigt
  const handleNavigateToFieldFromOversigt = (
    marknr: string,
    fieldData?: { marknr: string; cvr: string; areal: number; id: number; geometry?: string }
  ) => {
    console.log('=== App handleNavigateToFieldFromOversigt ===');
    console.log('marknr:', marknr);
    console.log('marknr type:', typeof marknr);
    console.log('marknr length:', (marknr as string).length);
    console.log('marknr charCodes:', Array.from(marknr as string).map(c => c.charCodeAt(0)));
    console.log('fieldData:', fieldData);

    if (fieldData) {
      console.log('fieldData.marknr:', (fieldData as any).marknr);
      console.log('fieldData.cvr:', (fieldData as any).cvr);
      console.log('fieldData.areal:', (fieldData as any).areal);
      console.log('fieldData.id:', (fieldData as any).id);
      console.log('fieldData.geometry exists?', !!(fieldData as any).geometry);
      console.log('fieldData.geometry length:', (fieldData as any).geometry?.length);
    }

    const context: FieldDetailContext = {
      fieldId: marknr,
      fieldData: fieldData ? {
        marknr: (fieldData as any).marknr,
        cvr: (fieldData as any).cvr,
        areal: (fieldData as any).areal,
        id: (fieldData as any).id,
        geometry: (fieldData as any).geometry
      } : undefined,
      source: 'oversigt'
    };

    console.log('Setting fieldDetailContext:', context);
    console.log('fieldDetailContext.fieldId:', (context as any).fieldId);
    console.log('fieldDetailContext.fieldId type:', typeof (context as any).fieldId);

    setFieldDetailContext(context);
    setCurrentView('fieldDetail');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setFieldDetailContext(null);
  };

  const handleBackToMyFields = () => {
    setCurrentView('myFields');
    setFieldDetailContext(null);
  };

  const handleBackToMineMarkerOversigt = () => {
    setCurrentView('mineMarkerOversigt');
    setFieldDetailContext(null);
  };

  const handleFieldDetailBack = () => {
    if ((fieldDetailContext as any)?.source === 'order') {
      handleBackToDashboard();
    } else if ((fieldDetailContext as any)?.source === 'oversigt') {
      handleBackToMineMarkerOversigt();
    } else {
      handleBackToMyFields();
    }
  };

  // Navigate to booking from MarkDetaljePage
  const handleNavigateToBooking = () => {
    setCurrentView('booking');
    setFieldDetailContext(null);
  };

  const handleLogout = () => {
    logout();
    setCurrentView('login');
    setGeojson(null);
    setCurrentCVR('');
    setSelectedFieldIds([]);
    setZoomLoadedFields([]);
    setBookingSelectedFields([]);
    setFieldDetailContext(null);
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const handleProfileUpdated = (profile: any) => {
    if (updateUser && (profile as any).name) {
      updateUser({ name: (profile as any).name });
    }
  };

  const handleFieldClick = (fieldId: string, fieldData?: any) => {
    setSelectedFieldIds(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter((id: any) => id !== fieldId);
      } else {
        if (fieldData && geojson) {
          const isOwnField = hasFeatureWithId((geojson as any).features || [], fieldId);
          if (!isOwnField) {
            setZoomLoadedFields(prev => {
              const exists = hasFeatureWithId(prev, fieldId);
              if (!exists) {
                return [...prev, fieldData];
              }
              return prev;
            });
          }
        }
        return [...prev, fieldId];
      }
    });
  };

  const handleBookingFieldClick = (fieldId: string, fieldData?: any) => {
    setBookingSelectedFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter((id: any) => id !== fieldId);
      } else {
        if (fieldData && geojson) {
          const isOwnField = hasFeatureWithId((geojson as any).features || [], fieldId);
          if (!isOwnField) {
            setZoomLoadedFields(prev => {
              const exists = hasFeatureWithId(prev, fieldId);
              if (!exists) {
                return [...prev, fieldData];
              }
              return prev;
            });
          }
        }
        return [...prev, fieldId];
      }
    });
  };

  const handleSelectedFieldsChange = (fieldIds: string[]) => {
    setSelectedFieldIds(fieldIds);
  };

  const handleBookingSelectionChange = (fieldIds: string[]) => {
    setBookingSelectedFields(fieldIds);
  };

  const handleRemoveZoomField = (fieldId: string) => {
    setZoomLoadedFields(prev => prev.filter((f: any) => getFieldId(f) !== fieldId));
    setSelectedFieldIds(prev => prev.filter((id: any) => id !== fieldId));
  };

  const highlightedZoomFields = selectedFieldIds.filter((id: any) =>
    zoomLoadedFields.some((f: any) => getFieldId(f) === id)
  );

  const handleSwitchToPasswordReset = () => {
    setCurrentView('resetRequest');
  };

  const handleResetRequestSuccess = (email: string) => {
    setResetEmail(email);
    setCurrentView('resetConfirm');
  };

  const handleResetConfirmSuccess = () => {
    setResetEmail('');
    setCurrentView('login');
  };

  const handleResetCancel = () => {
    setResetEmail('');
    setCurrentView('login');
  };

  const userCVRs = currentCVR ? currentCVR.split(',').map((cvr: any) => cvr.trim()) : [];

  if (!isAuthenticated) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/mark_m_felter_optimized.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Hero titel */}
        <div className="text-center mb-8">
          <h1 
            className="font-bold text-white mb-3"
            style={{
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              textShadow: '3px 3px 6px rgba(0,0,0,0.6)',
              letterSpacing: '0.02em'
            }}
          >
            FieldVision
          </h1>
          <p 
            className="text-white font-bold"
            style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
              letterSpacing: '0.02em'
            }}
          >
            Alle dine kort samlet ét sted
          </p>
          <p 
            className="text-white font-bold mt-1"
            style={{
              fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
              textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
              letterSpacing: '0.02em'
            }}
          >
            – nemt og enkelt
          </p>
        </div>
        <div className="w-full max-w-md">
          {currentView === 'login' && (
            <Login onSwitchToPasswordReset={handleSwitchToPasswordReset} />
          )}
          {currentView === 'register' && (
            <Register />
          )}
          {currentView === 'resetRequest' && (
            <ResetPasswordRequest
              onSuccess={handleResetRequestSuccess}
              onCancel={handleResetCancel}
            />
          )}
          {currentView === 'resetConfirm' && (
            <ResetPasswordConfirm
              email={resetEmail}
              onSuccess={handleResetConfirmSuccess}
              onCancel={handleResetCancel}
            />
          )}

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            {currentView === 'login' && (
              <>
                <button
                  onClick={() => setCurrentView('register')}
                  className="text-white hover:text-gray-200 hover:underline"
                  style={{textShadow: '2px 2px 4px rgba(0,0,0,0.6)'}}
                >
                  Har du ikke en konto? Registrer dig
                </button>
                <div className="text-center mt-8">
                  <p 
                    className="text-white font-light"
                    style={{
                      fontSize: 'clamp(1.2rem, 3vw, 2rem)',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      letterSpacing: '0.1em'
                    }}
                  >
                    Dansk Planteinspektion
                  </p>
                </div>
              </>
            )}
            {currentView === 'register' && (
              <button
                onClick={() => setCurrentView('login')}
                className="text-green-600 hover:text-green-700 hover:underline"
              >
                Har du allerede en konto? Log ind
              </button>
            )}
            {(currentView === 'resetRequest' || currentView === 'resetConfirm') && (
              <button
                onClick={() => setCurrentView('login')}
                className="text-green-600 hover:text-green-700 hover:underline"
              >
                Tilbage til login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header
        userName={(user as any)?.name}
        currentCVR={currentCVR || undefined}
        onHomeClick={handleBackToDashboard}
        onProfileClick={handleProfileClick}
        onLogoutClick={handleLogout}
      />

      <main className="app-main">
        {currentView === 'dashboard' && user && (
          <Dashboard
            user={{
              userId: (user as any).userId,
              email: (user as any).email,
              name: (user as any).name || ''
            }}
            onNavigate={handleNavigate}
            onNavigateToFieldDetail={handleNavigateToFieldDetail}
            currentCVR={currentCVR}
            selectedFieldIds={selectedFieldIds}
            zoomLoadedFields={zoomLoadedFields}
          />
        )}

        {currentView === 'mineMarkerOversigt' && (
          <MineMarkerOversigt
            onBack={handleBackToDashboard}
            onNavigateToField={handleNavigateToFieldFromOversigt}
            onNavigateToAddFields={() => setCurrentView('myFields')}
            userCVRs={userCVRs}
            zoomLoadedFields={zoomLoadedFields}
            selectedFieldIds={selectedFieldIds}
          />
        )}

        {currentView === 'mineLogfilerOversigt' && (
          <MineLogfilerOversigt
            onBack={() => setCurrentView('dashboard')}
            onNavigateToField={(marknr, fieldData) => {
              console.log('[App] Navigate to field from logfiler:', marknr, fieldData);
              if (fieldData) {
                setFieldDetailContext({
                  fieldId: marknr,
                  fieldData: {
                    marknr: fieldData.marknr,
                    cvr: fieldData.cvr,
                    areal: fieldData.areal
                  },
                  source: 'oversigt'
                });
              }
              setCurrentView('fieldDetail');
            }}
          />
        )}

        {currentView === 'myFields' && (
          <div className="split-view">
            <div className="sidebar-container">
              <button
                onClick={handleBackToMineMarkerOversigt}
                className="back-btn"
              >
                &larr; Tilbage til mine marker
              </button>
              <MyFields
                onFieldsLoaded={handleFieldsLoaded}
                selectedFieldIds={selectedFieldIds}
                onSelectedFieldsChange={handleSelectedFieldsChange}
                zoomLoadedFields={zoomLoadedFields}
                onRemoveZoomField={handleRemoveZoomField}
                onNavigateToFieldDetail={handleNavigateToFieldDetail}
              />
            </div>
            <div className="map-container">
              {true ? (
                <MapView
                  fieldsData={geojson}
                  selectedFields={selectedFieldIds}
                  onFieldClick={handleFieldClick}
                  enableZoomLoading={true}
                  externalZoomFields={zoomLoadedFields}
                />
              ) : (
                <div className="empty-map">
                  <p>Valg dine CVR'er for at se marker pa kortet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {currentView === 'booking' && (
          <div className="split-view">
            <div className="sidebar-container">
              <button
                onClick={handleBackToDashboard}
                className="back-btn"
              >
                &larr; Tilbage til dashboard
              </button>
              <BookingWizard
                fieldsData={geojson}
                zoomLoadedFields={zoomLoadedFields}
                bookingSelectedFields={bookingSelectedFields}
                onBookingSelectionChange={handleBookingSelectionChange}
                highlightedZoomFields={highlightedZoomFields}
                onNavigateToMyFields={() => setCurrentView('mineMarkerOversigt')}
              />
            </div>
            <div className="map-container">
              {true ? (
                <MapView
                  fieldsData={geojson}
                  selectedFields={bookingSelectedFields}
                  onFieldClick={handleBookingFieldClick}
                  enableZoomLoading={true}
                  externalZoomFields={zoomLoadedFields}
                  highlightedZoomFields={highlightedZoomFields}
                />
              ) : (
                <div className="empty-map">
                  <p>Ingen marker at vise - ga til Mine Marker forst</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DEBUG: Sender fieldData med geometry og onNavigateToBooking */}
        {currentView === 'fieldDetail' && fieldDetailContext && (
          <MarkDetaljePage
            fieldId={(fieldDetailContext as any).fieldId}
            orderId={(fieldDetailContext as any).orderId}
            field={(fieldDetailContext as any).fieldData}
            order={(fieldDetailContext as any).orderData}
            onBack={handleFieldDetailBack}
            onNavigateToBooking={handleNavigateToBooking}
          />
        )}
      </main>

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onProfileUpdated={handleProfileUpdated}
      />

      <style>{`
        .app-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .app-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .split-view {
          display: grid;
          grid-template-columns: 400px 1fr;
          height: 100%;
          flex: 1;
        }

        .sidebar-container {
          background-color: #f5f5f5;
          padding: 20px;
          overflow-y: auto;
          border-right: 1px solid #ddd;
        }

        .map-container {
          position: relative;
          height: 100%;
        }

        .empty-map {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #666;
          font-size: 16px;
        }

        .back-btn {
          background-color: #ffffff;
          border: 1px solid #ddd;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          margin-bottom: 20px;
          transition: background-color 0.2s;
          width: 100%;
        }

        .back-btn:hover {
          background-color: #e0e0e0;
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
